import React, { useCallback } from 'react';

import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
  WpDropdownMenuTitle,
} from 'Wpds/components/dropdown';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { getSourcePage } from 'Shared/utils/getSourcePage';

import { GoalItem } from 'Shared/constants/types';

import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Constants
import Colors from 'Wpds/colors/ColorConstants';

const preventBubbleEvent = e => e.stopPropagation();

type GoalCardDropdownProps = {
  slug: string;
  goal: GoalItem;
  handleMarkComplete: Function;
  handleMarkIncomplete: Function;
  handleChangeStatus: Function;
  redirectToGoalDetailPage: Function;
};

export default function GoalCardDropdown({
  slug,
  goal,
  handleMarkComplete,
  handleMarkIncomplete,
  handleChangeStatus,
  redirectToGoalDetailPage,
}: GoalCardDropdownProps) {
  const isComplete = goal.complete;

  const { id: userId } = useCurrentUser();
  const canChangeStatus = userId === goal.assigneeId || userId === goal.assignerId;

  const handleSetStatusClick = useCallback(
    status => {
      handleChangeStatus(slug, status);
    },
    [handleChangeStatus, slug]
  );

  const handleMarkCompleteActionClick = useCallback(() => {
    handleMarkComplete(slug);
  }, [handleMarkComplete, slug]);

  const handleMarkIncompleteActionClick = useCallback(() => {
    handleMarkIncomplete(slug);
  }, [handleMarkIncomplete, slug]);

  const handleViewDetailsActionClick = useCallback(() => {
    redirectToGoalDetailPage(slug);
  }, [redirectToGoalDetailPage, slug]);

  const trackDropdownClick = (isOpen: boolean) => {
    if (isOpen)
      (window as any).analytics.track('Opened Item Menu', { source_page: getSourcePage(), item_type: 'goalCard' });
  };

  return (
    <div
      onClick={e => {
        preventBubbleEvent(e);
      }}
    >
      <WpDropdown placement="bottom-end" onClickCallback={trackDropdownClick}>
        <WpDropdownToggle>
          <WpButton size="sm" variant="tertiary" color="primary" qaTest="btn-goal-card-dropdown">
            <WpIcon name="ellipsis-h-alt" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {canChangeStatus && !isComplete && (
            <>
              <WpDropdownMenuTitle title="Set Status" />
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('on_track')}
                qaTest="goal-card-dropdown-status-change-on-track"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.green[500]} />
                On track
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('at_risk')}
                qaTest="goal-card-dropdown-status-change-at-risk"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.yellow[500]} />
                At risk
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('missed')}
                qaTest="goal-card-dropdown-status-change-missed"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.red[500]} />
                Missed
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('achieved')}
                qaTest="goal-card-dropdown-status-change-achieved"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.purple[500]} />
                Achieved
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={() => handleSetStatusClick('no_status')}
                qaTest="goal-card-dropdown-status-change-no-status"
              >
                <WpIcon prefix="fas" name="circle" color={Colors.blue[500]} />
                No status
              </WpDropdownMenuItem>
              <WpDropdownMenuDivider />
            </>
          )}
          {canChangeStatus && (
            <>
              {isComplete ? (
                <WpDropdownMenuItem onClick={handleMarkIncompleteActionClick}>Mark incomplete</WpDropdownMenuItem>
              ) : (
                <WpDropdownMenuItem onClick={handleMarkCompleteActionClick}>Mark complete</WpDropdownMenuItem>
              )}
            </>
          )}
          <WpDropdownMenuItem onClick={handleViewDetailsActionClick}>View details</WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
    </div>
  );
}
